/**
 * Typefaces the branding page offers in one click.
 *
 * Each is a Google Fonts family with a real fallback stack — the same shape
 * `tokens.json` uses for `brand.font.*` — so choosing one is exactly a token
 * edit. Google Fonts because the shipped theme already loads from it, so the
 * page's CSP admits these hosts and a preset previews before it is saved; a
 * custom host appears after the next page load, once the policy names it.
 */

export type FontRole = "display" | "body" | "mono";

export interface FontSpec {
  family: string;
  stack: string;
  source: string | null;
}

export interface FontPreset extends FontSpec {
  kind: "serif" | "sans" | "mono";
}

const SERIF = "Georgia, 'Times New Roman', serif";
const SANS = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

function google(family: string, axes: string, kind: FontPreset["kind"]): FontPreset {
  const fallback = kind === "serif" ? SERIF : kind === "mono" ? MONO : SANS;
  return {
    family,
    kind,
    stack: `'${family}', ${fallback}`,
    source: `https://fonts.googleapis.com/css2?family=${family.replaceAll(" ", "+")}:${axes}&display=swap`,
  };
}

export const FONT_PRESETS: FontPreset[] = [
  google("EB Garamond", "ital,wght@0,400..700;1,400..700", "serif"),
  google("Fraunces", "wght@400..700", "serif"),
  google("Lora", "ital,wght@0,400..700;1,400..700", "serif"),
  google("Merriweather", "wght@400;700", "serif"),
  google("Playfair Display", "wght@400..700", "serif"),
  google("Source Serif 4", "wght@400..700", "serif"),
  google("DM Sans", "wght@400..700", "sans"),
  google("IBM Plex Sans", "wght@400;500;600;700", "sans"),
  google("Inter", "wght@300..800", "sans"),
  google("Lato", "wght@400;700", "sans"),
  google("Montserrat", "wght@400..700", "sans"),
  google("Nunito Sans", "wght@400..800", "sans"),
  google("Open Sans", "wght@400..700", "sans"),
  google("Public Sans", "ital,wght@0,300..800;1,300..800", "sans"),
  google("Roboto", "wght@400;500;700", "sans"),
  google("Source Sans 3", "wght@400..700", "sans"),
  google("Work Sans", "wght@400..700", "sans"),
  google("Fira Code", "wght@400..700", "mono"),
  google("IBM Plex Mono", "wght@400;500;700", "mono"),
  google("JetBrains Mono", "wght@400;500;700", "mono"),
  google("Roboto Mono", "wght@400..700", "mono"),
  google("Source Code Pro", "wght@400..700", "mono"),
];

/** What each role is for, in the words the page uses. */
export const FONT_ROLES: { role: FontRole; label: string; hint: string; sample: string }[] = [
  {
    role: "display",
    label: "Headings",
    hint: "Page titles, report titles and the PDF letterhead.",
    sample: "Quarterly revenue by region",
  },
  {
    role: "body",
    label: "Text",
    hint: "Everything else: answers, tables, labels and chart axes.",
    sample: "Revenue rose 12% on the quarter, led by the coastal stores.",
  },
  {
    role: "mono",
    label: "Code",
    hint: "SQL, and anything else shown exactly as written.",
    sample: "SELECT region, SUM(total) FROM orders GROUP BY 1",
  },
];
